import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { MaiChatEvents } from '~/analytics/maiChatEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useAppDispatch } from '~/store/hooks';
import { maiApi, maiHistoryArgs } from '~/store/services/mai';
import { SYSTEM_TIME_ZONE } from '~/lib/util/timezone.util';

import { MAI_CHAT_ERROR_KIND_LABELS } from './maiChat.constants';
import {
  appendTextSegment,
  appendToolCallSegment,
  applyToolResults,
  dedupeCitations,
  settlePendingToolCalls,
  summarizeResponse,
} from './maiChat.util';
import { MaiChatErrorKind, MaiMessage } from './maiChat.types';
import {
  DeltaFrame,
  DoneFrame,
  ToolCallFrame,
  ToolResultFrame,
} from './maiSocket.frames';
import { useMaiSocket } from './useMaiSocket';

export type MaiChatPhase = 'idle' | 'submitting' | 'streaming' | 'error';

const STREAM_INACTIVITY_MS = 90_000;

export interface MaiTurnState {
  phase: MaiChatPhase;
  errorKind: MaiChatErrorKind | null;
  turnKey: number;
}

const IDLE_TURN: MaiTurnState = { phase: 'idle', errorKind: null, turnKey: 0 };
Object.freeze(IDLE_TURN);

interface MutableTurn {
  connectionEpoch: number;
  turnSeq: number;
  streamingId: string | null;
  lastUserText: string;
  phase: MaiChatPhase;
  pendingTokens: string;
  flushTimer: ReturnType<typeof setTimeout> | null;
  watchdogTimer: ReturnType<typeof setTimeout> | null;
  // Owned by the panel and handed over on each turn, so the events reported from
  // here join the panel's on one conversation.
  conversationId: string;
  startedAtMs: number | null;
  firstRenderMs: number | null;
}

const createTurn = (): MutableTurn => ({
  connectionEpoch: 0,
  turnSeq: 0,
  streamingId: null,
  lastUserText: '',
  phase: 'idle',
  pendingTokens: '',
  flushTimer: null,
  watchdogTimer: null,
  conversationId: '',
  startedAtMs: null,
  firstRenderMs: null,
});

export interface MaiChatSession {
  getTurnState: (patientId: string) => MaiTurnState;
  patchMessages: (
    patientId: string,
    recipe: (prevList: MaiMessage[]) => MaiMessage[],
  ) => void;
  startTurn: (patientId: string, text: string, conversationId: string) => void;
  retryTurn: (patientId: string) => void;
  beginPatientSession: (patientId: string) => void;
  subscribeToCacheWrites: (listener: () => void) => () => void;
  getCacheWriteVersion: () => number;
}

const MaiChatSessionContext = createContext<MaiChatSession | null>(null);

export const MaiChatSessionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useAppDispatch();
  const analytics = useAnalytics();

  const turnsRef = useRef(new Map<string, MutableTurn>());
  const [turnStates, setTurnStates] = useState<Record<string, MaiTurnState>>(
    {},
  );

  const activePatientIdRef = useRef<string | null>(null);
  const connectionPatientIdRef = useRef<string | null>(null);
  const connectionEpochRef = useRef(0);

  const getTurn = useCallback((patientId: string): MutableTurn => {
    const existing = turnsRef.current.get(patientId);
    if (existing) return existing;
    const created = createTurn();
    turnsRef.current.set(patientId, created);
    return created;
  }, []);

  const activeFrameTarget = useCallback((): string | null => {
    const patientId = activePatientIdRef.current;
    if (patientId === null) return null;
    const turn = turnsRef.current.get(patientId);
    if (turn === undefined) return null;
    return turn.connectionEpoch === connectionEpochRef.current
      ? patientId
      : null;
  }, []);

  const patchState = useCallback(
    (patientId: string, patch: Partial<MaiTurnState>) => {
      setTurnStates((prev) => ({
        ...prev,
        [patientId]: { ...(prev[patientId] ?? IDLE_TURN), ...patch },
      }));
    },
    [],
  );

  const setPhase = useCallback(
    (patientId: string, next: MaiChatPhase) => {
      getTurn(patientId).phase = next;
      patchState(patientId, { phase: next });
    },
    [getTurn, patchState],
  );

  const cacheWriteVersionRef = useRef(0);
  const cacheWriteListenersRef = useRef(new Set<() => void>());

  const subscribeToCacheWrites = useCallback((listener: () => void) => {
    cacheWriteListenersRef.current.add(listener);
    return () => {
      cacheWriteListenersRef.current.delete(listener);
    };
  }, []);

  const getCacheWriteVersion = useCallback(
    () => cacheWriteVersionRef.current,
    [],
  );

  const notifyCacheWrite = useCallback(() => {
    cacheWriteVersionRef.current += 1;
    cacheWriteListenersRef.current.forEach((listener) => listener());
  }, []);

  const patchMessages = useCallback(
    (patientId: string, recipe: (prevList: MaiMessage[]) => MaiMessage[]) => {
      const historyArgs = maiHistoryArgs(patientId);
      dispatch((patchDispatch, getState) => {
        const entry =
          maiApi.endpoints.getMaiMessages.select(historyArgs)(getState());
        if (entry.data === undefined) {
          patchDispatch(
            maiApi.util.upsertQueryEntries([
              {
                endpointName: 'getMaiMessages',
                arg: historyArgs,
                value: recipe([]),
              },
            ]),
          );
          return;
        }
        patchDispatch(
          maiApi.util.updateQueryData(
            'getMaiMessages',
            historyArgs,
            (draft) => {
              return recipe(draft as MaiMessage[]);
            },
          ),
        );
      });
      notifyCacheWrite();
    },
    [dispatch, notifyCacheWrite],
  );

  const updateMessageById = useCallback(
    (
      patientId: string,
      id: string,
      updater: (message: MaiMessage) => MaiMessage,
    ) => {
      patchMessages(patientId, (prevList) =>
        prevList.map((message) =>
          message.id === id ? updater(message) : message,
        ),
      );
    },
    [patchMessages],
  );

  const clearFlushTimer = useCallback((turn: MutableTurn) => {
    if (turn.flushTimer !== null) {
      clearTimeout(turn.flushTimer);
      turn.flushTimer = null;
    }
  }, []);

  const clearWatchdog = useCallback((turn: MutableTurn) => {
    if (turn.watchdogTimer !== null) {
      clearTimeout(turn.watchdogTimer);
      turn.watchdogTimer = null;
    }
  }, []);

  const openStreamingBubble = useCallback(
    (patientId: string, segments: MaiMessage['segments']) => {
      const id = `ai-${uuidv4()}`;
      const turn = getTurn(patientId);
      if (turn.firstRenderMs === null) {
        turn.firstRenderMs = Date.now();
      }
      turn.streamingId = id;
      setPhase(patientId, 'streaming');
      patchMessages(patientId, (prevList) => [
        ...prevList,
        {
          id,
          role: 'ai',
          status: 'streaming',
          createdAt: new Date().toISOString(),
          segments,
        },
      ]);
      return id;
    },
    [getTurn, patchMessages, setPhase],
  );

  const flushPendingTokens = useCallback(
    (patientId: string, seq: number, id: string) => {
      const turn = getTurn(patientId);
      const tokens = turn.pendingTokens;
      turn.pendingTokens = '';
      if (seq !== turn.turnSeq || tokens.length === 0) return;
      updateMessageById(patientId, id, (message) => ({
        ...message,
        segments: appendTextSegment(message.segments ?? [], tokens),
      }));
    },
    [getTurn, updateMessageById],
  );

  const finalizeStream = useCallback(
    (
      patientId: string,
      updater: (message: MaiMessage) => MaiMessage,
    ): boolean => {
      const turn = getTurn(patientId);
      const id = turn.streamingId;
      if (id === null) return false;
      clearFlushTimer(turn);
      flushPendingTokens(patientId, turn.turnSeq, id);
      updateMessageById(patientId, id, updater);
      turn.streamingId = null;
      return true;
    },
    [clearFlushTimer, flushPendingTokens, getTurn, updateMessageById],
  );

  const failTurn = useCallback(
    (patientId: string, kind: MaiChatErrorKind) => {
      const turn = getTurn(patientId);
      if (turn.phase !== 'submitting' && turn.phase !== 'streaming') return;
      clearWatchdog(turn);
      finalizeStream(patientId, (message) => ({
        ...message,
        status: 'error',
        segments: settlePendingToolCalls(message.segments ?? []),
      }));
      if (activePatientIdRef.current === patientId) {
        activePatientIdRef.current = null;
      }
      turn.phase = 'error';
      patchState(patientId, { phase: 'error', errorKind: kind });
      analytics?.trackEvent(MaiChatEvents.errorShown, {
        patientId,
        'Conversation Id': turn.conversationId,
        'Error Kind': MAI_CHAT_ERROR_KIND_LABELS[kind],
      });
    },
    [analytics, clearWatchdog, finalizeStream, getTurn, patchState],
  );

  const armWatchdog = useCallback(
    (patientId: string) => {
      const turn = getTurn(patientId);
      clearWatchdog(turn);
      turn.watchdogTimer = setTimeout(() => {
        failTurn(patientId, 'network');
      }, STREAM_INACTIVITY_MS);
    },
    [clearWatchdog, failTurn, getTurn],
  );

  const handleDelta = useCallback(
    (frame: DeltaFrame) => {
      const patientId = activeFrameTarget();
      if (patientId === null) return;
      const turn = getTurn(patientId);

      if (turn.streamingId === null) {
        if (frame.index !== 0 || turn.phase !== 'submitting') return;
        openStreamingBubble(patientId, [{ kind: 'text', text: frame.token }]);
        return;
      }

      turn.pendingTokens += frame.token;
      if (turn.flushTimer !== null) return;
      const seq = turn.turnSeq;
      const id = turn.streamingId;
      turn.flushTimer = setTimeout(() => {
        turn.flushTimer = null;
        flushPendingTokens(patientId, seq, id);
      }, 0);
    },
    [activeFrameTarget, flushPendingTokens, getTurn, openStreamingBubble],
  );

  const handleToolCall = useCallback(
    (frame: ToolCallFrame) => {
      const patientId = activeFrameTarget();
      if (patientId === null) return;
      const turn = getTurn(patientId);
      const toolCall = {
        toolUseId: frame.toolUseId,
        name: frame.name,
        status: 'pending' as const,
      };

      if (turn.streamingId === null) {
        if (turn.phase !== 'submitting') return;
        openStreamingBubble(patientId, [
          { kind: 'tools', toolCalls: [toolCall] },
        ]);
        return;
      }
      clearFlushTimer(turn);
      const buffered = turn.pendingTokens;
      turn.pendingTokens = '';
      updateMessageById(patientId, turn.streamingId, (message) => {
        const withText = buffered
          ? appendTextSegment(message.segments ?? [], buffered)
          : (message.segments ?? []);
        return {
          ...message,
          segments: appendToolCallSegment(withText, toolCall),
        };
      });
    },
    [
      activeFrameTarget,
      clearFlushTimer,
      getTurn,
      openStreamingBubble,
      updateMessageById,
    ],
  );

  const handleToolResult = useCallback(
    (frame: ToolResultFrame) => {
      const patientId = activeFrameTarget();
      if (patientId === null) return;
      const id = getTurn(patientId).streamingId;
      if (id === null) return;
      const sources = frame.results.flatMap((result) => result.sources);
      updateMessageById(patientId, id, (message) => ({
        ...message,
        segments: applyToolResults(message.segments ?? [], frame.results),
        citations:
          sources.length > 0
            ? dedupeCitations(sources, message.citations)
            : message.citations,
      }));
    },
    [activeFrameTarget, getTurn, updateMessageById],
  );

  const handleDone = useCallback(
    (frame: DoneFrame) => {
      const patientId = activeFrameTarget();
      if (patientId === null) return;
      let completed: MaiMessage | null = null;
      const finalized = finalizeStream(patientId, (message) => {
        completed = {
          ...message,
          status: 'sent',
          // The server writes the assistant row as the turn completes, so this
          // matches the time history shows after a reload.
          createdAt: new Date().toISOString(),
          messageId: frame.messageId,
          segments: settlePendingToolCalls(message.segments ?? []),
        };
        return completed;
      });
      if (!finalized) return;
      const turn = getTurn(patientId);
      clearWatchdog(turn);
      activePatientIdRef.current = null;
      setPhase(patientId, 'idle');
      if (completed === null) return;
      const { conversationId, firstRenderMs, startedAtMs } = turn;
      const summary = summarizeResponse(completed);
      analytics?.trackEvent(MaiChatEvents.responseCompleted, {
        patientId,
        'Conversation Id': conversationId,
        // Omitted rather than zeroed when unknown: a fake 0 would read on the
        // latency tiles as a speed-up.
        ...(startedAtMs === null
          ? {}
          : { 'Latency Ms': Date.now() - startedAtMs }),
        ...(startedAtMs === null || firstRenderMs === null
          ? {}
          : { 'Time To First Render Ms': firstRenderMs - startedAtMs }),
        'Tool Count': summary.toolCount,
        'Tool Names': summary.toolNames,
        'Chart Citation Count': summary.chartCitationCount,
        'KB Citation Count': summary.kbCitationCount,
        'Malformed Citation Count': summary.malformedCitationCount,
        'Segment Count': summary.segmentCount,
      });
    },
    [
      activeFrameTarget,
      analytics,
      clearWatchdog,
      finalizeStream,
      getTurn,
      setPhase,
    ],
  );

  const handleActivity = useCallback(() => {
    const patientId = activeFrameTarget();
    if (patientId === null) return;
    const phase = getTurn(patientId).phase;
    if (phase === 'submitting' || phase === 'streaming') {
      armWatchdog(patientId);
    }
  }, [activeFrameTarget, armWatchdog, getTurn]);

  const handleError = useCallback(() => {
    const patientId = activeFrameTarget();
    if (patientId !== null) failTurn(patientId, 'agent');
  }, [activeFrameTarget, failTurn]);

  const handleConnectionError = useCallback(() => {
    const patientId = activeFrameTarget();
    if (patientId !== null) failTurn(patientId, 'network');
  }, [activeFrameTarget, failTurn]);

  const socketCallbacks = useMemo(
    () => ({
      onDelta: handleDelta,
      onToolCall: handleToolCall,
      onToolResult: handleToolResult,
      onDone: handleDone,
      onError: handleError,
      onConnectionError: handleConnectionError,
      onActivity: handleActivity,
    }),
    [
      handleActivity,
      handleConnectionError,
      handleDelta,
      handleDone,
      handleError,
      handleToolCall,
      handleToolResult,
    ],
  );

  const { send, ensureConnected, resetConnection } =
    useMaiSocket(socketCallbacks);

  const beginPatientSession = useCallback(
    (patientId: string) => {
      const previous = connectionPatientIdRef.current;
      if (previous !== null && previous !== patientId) {
        const inFlight = activePatientIdRef.current;
        if (inFlight !== null) failTurn(inFlight, 'interrupted');

        // Turn state is kept (keyed by patientId, so it cannot surface under the
        // new chart): dropping it would erase the error phase and lastUserText
        // that keep an interrupted stream visibly failed and retryable.
        const stale = turnsRef.current.get(previous);
        if (stale !== undefined) {
          clearFlushTimer(stale);
          clearWatchdog(stale);
        }

        connectionEpochRef.current += 1;
        activePatientIdRef.current = null;
        resetConnection();
      }
      connectionPatientIdRef.current = patientId;
      ensureConnected();
    },
    [
      clearFlushTimer,
      clearWatchdog,
      ensureConnected,
      failTurn,
      resetConnection,
    ],
  );

  const startTurn = useCallback(
    (patientId: string, text: string, conversationId: string) => {
      if (connectionPatientIdRef.current !== patientId) {
        beginPatientSession(patientId);
      }

      const turn = getTurn(patientId);
      turn.turnSeq += 1;
      turn.streamingId = null;
      turn.pendingTokens = '';
      turn.lastUserText = text;
      turn.conversationId = conversationId;
      turn.startedAtMs = Date.now();
      turn.firstRenderMs = null;
      turn.connectionEpoch = connectionEpochRef.current;
      clearFlushTimer(turn);
      activePatientIdRef.current = patientId;

      turn.phase = 'submitting';
      setTurnStates((prev) => {
        const current = prev[patientId] ?? IDLE_TURN;
        return {
          ...prev,
          [patientId]: {
            phase: 'submitting',
            errorKind: null,
            turnKey: current.turnKey + 1,
          },
        };
      });
      armWatchdog(patientId);

      send({
        action: 'sendMessage',
        patientId,
        text,
        timezone: SYSTEM_TIME_ZONE,
      });
    },
    [armWatchdog, beginPatientSession, clearFlushTimer, getTurn, send],
  );

  const retryTurn = useCallback(
    (patientId: string) => {
      const { conversationId, lastUserText } = getTurn(patientId);
      if (lastUserText.length === 0) return;
      patchMessages(patientId, (prevList) =>
        prevList.filter((message) => message.status !== 'error'),
      );
      startTurn(patientId, lastUserText, conversationId);
    },
    [getTurn, patchMessages, startTurn],
  );

  useEffect(() => {
    const turns = turnsRef.current;
    return () => {
      turns.forEach((turn) => {
        if (turn.flushTimer !== null) clearTimeout(turn.flushTimer);
        if (turn.watchdogTimer !== null) clearTimeout(turn.watchdogTimer);
      });
      turns.clear();
      activePatientIdRef.current = null;
      connectionPatientIdRef.current = null;
      resetConnection();
    };
  }, [resetConnection]);

  const getTurnState = useCallback(
    (patientId: string): MaiTurnState => turnStates[patientId] ?? IDLE_TURN,
    [turnStates],
  );

  const value = useMemo<MaiChatSession>(
    () => ({
      beginPatientSession,
      getCacheWriteVersion,
      getTurnState,
      patchMessages,
      retryTurn,
      startTurn,
      subscribeToCacheWrites,
    }),
    [
      beginPatientSession,
      getCacheWriteVersion,
      getTurnState,
      patchMessages,
      retryTurn,
      startTurn,
      subscribeToCacheWrites,
    ],
  );

  return (
    <MaiChatSessionContext.Provider value={value}>
      {children}
    </MaiChatSessionContext.Provider>
  );
};

export const useMaiChatSession = (): MaiChatSession => {
  const session = useContext(MaiChatSessionContext);
  if (session === null) {
    throw new Error(
      'useMaiChatSession must be used within a MaiChatSessionProvider',
    );
  }
  return session;
};
